import { useCallback, useMemo } from 'react'

import { DrawingParameterMeasurementLabel, DrawingType } from '@editor/interfaces/drawing'
import { CanvasEventsExtra } from '@editor/interfaces/editor'
import { BLUEPRINT_TOOL_LABELS } from '@editor/pages/projects/blueprint-editor/constants'
import { BlueprintEditorTools } from '@editor/pages/projects/blueprint-editor/definitions'
import usePointSelection from '@editor/pages/projects/blueprint-editor/hooks/usePointSelection'
import { addDrawing, removeDrawings } from '@editor/pages/projects/blueprint-editor/store/drawings'
import { RootState, useAppDispatch } from '@editor/store/app'
import { setWorkingElementId } from '@editor/store/tool'
import { useSelector } from 'react-redux'
import { Matrix4Tuple } from 'three'

import { getLastCount, getNewOrder } from '../../utils'

const useEventHandlers = () => {
  const { getIntersection } = usePointSelection()

  // Store
  const dispatch = useAppDispatch()
  const workingElementId = useSelector((state: RootState) => state.toolUtil.core.workingElementId)
  const editingElementId = useSelector((state: RootState) => state.toolUtil.core.editingElementId)
  const selectedTool = useSelector((state: RootState) => state.blueprint.editor.selectedTool)
  const drawings = useSelector((state: RootState) => state.blueprint.drawings.document.drawings)
  const defaultStyles = useSelector((state: RootState) => state.blueprint.drawings.defaultStyles)

  // Vars
  const isSelectedTool = useMemo(() => selectedTool === BlueprintEditorTools.MeasurementLabel, [selectedTool])

  const handleMouseUp = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, { isDragged }: CanvasEventsExtra) => {
      // If we're editing or working on an existing element, don't add a new one
      if (isDragged || workingElementId || !isSelectedTool || editingElementId) return

      const point = getIntersection(event)
      if (!point) return

      // add the first point into the transformation matrix
      // prettier-ignore
      const transformation: Matrix4Tuple = [
        1, 0, 0, 0, 
        0, 1, 0, 0, 
        0, 0, 1, 0, 
        point[0], point[1], point[2], 1
      ]
      const drawing_parameters: DrawingParameterMeasurementLabel = {
        uuid: crypto.randomUUID(),
        order: getNewOrder(drawings),
        points: [point, point],
        transformation,
        styles: defaultStyles,
      }

      dispatch(
        addDrawing({
          name: `${BLUEPRINT_TOOL_LABELS['measurement-label']} ${
            getLastCount(
              drawings.map((p) => p.name),
              BLUEPRINT_TOOL_LABELS['measurement-label'],
            ) + 1
          }`,
          drawing_type: DrawingType.MeasurementLabel,
          drawing_parameters,
        }),
      )
      dispatch(setWorkingElementId(drawing_parameters.uuid))
    },
    [workingElementId, editingElementId, isSelectedTool, drawings, defaultStyles, getIntersection, dispatch],
  )

  const handleCancel = useCallback(() => {
    if (!isSelectedTool) return

    if (workingElementId) {
      dispatch(removeDrawings([workingElementId]))
      dispatch(setWorkingElementId(undefined))
    }
  }, [isSelectedTool, workingElementId, dispatch])

  return {
    handleMouseUp,
    handleCancel,
  }
}

export default useEventHandlers
